import { Fragment, PropsWithChildren, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  Box,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { compatWrapper } from '@backstage/core-compat-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';

import { ConfidenceProvider, useFlag } from '@spotify-confidence/react';
import {
  ConfidenceCoreProvider,
  LoginPage,
  ResourceLinkExtensions,
  useAuth,
  useFeatureFlag,
  useRegion,
} from '@spotify/confidence-core-react';
import {
  getTypeOrNull,
  useAccountSetupQuery,
} from '@spotify/confidence-graphql';
import {
  EntityResourceLinkExtension,
  FactTableResourceLinkExtension,
} from '@spotify/plugin-confidence-admin';
import {
  AppAccountRedirect,
  AppRedirectPage,
  useAppRedirect,
} from '@spotify/plugin-confidence-app-react';
import { FlagResourceLinkExtension } from '@spotify/plugin-confidence-flags';
import { IdentityResourceLinkExtension } from '@spotify/plugin-confidence-iam';
import {
  MeasurementResourceLinkExtension,
  MetricResourceLinkExtension,
} from '@spotify/plugin-confidence-metrics';
import { SurfaceResourceLinkExtension } from '@spotify/plugin-confidence-surfaces';
import {
  PageTrackingGuard,
  PageTrackingProvider,
  useAutoTracking,
} from '@spotify/plugin-confidence-tracking-react';
import { WorkflowInstanceResourceLinkExtension } from '@spotify/plugin-confidence-workflows';

import { confidenceApi } from '../apis/Apis';
import { FlagContext } from './FlagContext';
import { Header } from './Header';

const useStyles = makeStyles(theme => ({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
  },
  blockedBannerContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 1200,
    width: '100%',
  },
  blockedBanner: {
    borderRadius: 0,
    justifyContent: 'center',
    '& .MuiAlert-message': {
      textAlign: 'center',
    },
  },
}));

// Routes that don't require authentication
const PUBLIC_ROUTES = [
  '/login',
  '/invitation',
  '/signup',
  '/beta',
  '/email-verified',
  '/override',
];

const isPublicRoute = (pathname: string) =>
  PUBLIC_ROUTES.some(route => pathname.startsWith(route));

const resourceLinkExtensions = (
  <ResourceLinkExtensions>
    <FlagResourceLinkExtension />
    <IdentityResourceLinkExtension />
    <MetricResourceLinkExtension />
    <MeasurementResourceLinkExtension />
    <EntityResourceLinkExtension />
    <FactTableResourceLinkExtension />
    <SurfaceResourceLinkExtension />
    <WorkflowInstanceResourceLinkExtension />
  </ResourceLinkExtensions>
);

const LoadingPage = () => {
  const classes = useStyles();
  return (
    <div className={classes.loadingContainer}>
      <CircularProgress />
    </div>
  );
};

const BlockedAccountBanner = () => {
  const classes = useStyles();
  const { data } = useAccountSetupQuery({ fetchPolicy: 'cache-and-network' });
  const currentAccount = getTypeOrNull(data?.currentAccount);

  if (!currentAccount?.blocked) {
    return null;
  }

  return (
    <Box className={classes.blockedBannerContainer}>
      <Alert
        severity="error"
        variant="filled"
        className={classes.blockedBanner}
      >
        <Typography variant="body1">
          <strong>This account has been blocked.</strong> Please contact support
          for assistance.
        </Typography>
      </Alert>
    </Box>
  );
};

const Root = ({ children }: PropsWithChildren) => {
  const {
    isAuthenticated,
    isLoading,
    loginWithPopup,
    loginWithRedirect,
    error,
  } = useAuth();
  const { enabled: portalNavigationEnabled } = useFeatureFlag<{
    enabled: boolean;
  }>('portal-navigation', { enabled: false });
  const preferredUrl = useFlag('preferred-app-for-org.preferred-url', '');
  const appRedirect = useAppRedirect(preferredUrl);
  const region = useRegion();
  const autoTrackRef = useAutoTracking();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const shouldLogin = searchParams.get('login') === 'true';
  const organization = searchParams.get('organization') ?? undefined;

  // Handle auto-login redirect (e.g., from invitation links with ?login=true&organization=xxx)
  useEffect(() => {
    if (!isLoading && !isAuthenticated && shouldLogin && organization) {
      loginWithRedirect({
        authorizationParams: {
          organization,
        },
        appState: {
          returnTo: window.location.pathname,
        },
      });
    }
  }, [
    shouldLogin,
    organization,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
  ]);

  // Public routes render immediately without auth check
  if (isPublicRoute(location.pathname)) {
    return <Fragment>{children}</Fragment>;
  }

  // Protected routes: wait for auth AND token delivery before rendering anything.
  // Auth0's isAuthenticated can be true (from cache) before the token is actually
  // fetched and delivered to Apollo. The region is set when the token is received,
  // so we use it as a proxy for "token is ready".
  if (isLoading || (isAuthenticated && !region)) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return (
      <LoginPage
        onLogin={() => loginWithPopup(undefined, { timeoutInSeconds: 600 })}
        loading={isLoading}
        error={error}
      />
    );
  }

  if (appRedirect.shouldRedirect) {
    return <AppRedirectPage targetUrl={appRedirect.targetUrl} />;
  }

  return (
    <div ref={autoTrackRef}>
      <PageTrackingGuard />
      {resourceLinkExtensions}
      {!portalNavigationEnabled && <Header />}
      <BlockedAccountBanner />
      <AppAccountRedirect />
      {children}
    </div>
  );
};

export const RootWrapper = AppRootWrapperBlueprint.make({
  name: 'AppRootWrapper',
  params: {
    component: ({ children }) => {
      return compatWrapper(
        <ConfidenceProvider confidence={confidenceApi}>
          <PageTrackingProvider>
            <ConfidenceCoreProvider>
              <FlagContext>
                <Root>{children}</Root>
              </FlagContext>
            </ConfidenceCoreProvider>
          </PageTrackingProvider>
        </ConfidenceProvider>,
      );
    },
  },
});
